export const PATHS = {
  MAIN: "/",
  PROPERTY: "/property",
  /** Alt phones 2–5 RealValidito DNC status (one row per number). */
  DNC_CHECK: "/dnc-check",
  CALLS: "/calls",
  WEBHOOK_LOGS: "/webhook-logs",
  SETTINGS: "/settings",
  VAPI_EMULATOR_ANSWERS: "/vapi-emulator-answers",
  VAPI_EMULATOR_LOGS: "/vapi-emulator-logs",
  CALENDAR: "/calendar",
  CALLING_CALENDAR: "/calling-calendar",
  LOST_LEADS: "/lost-leads",
  ANALYTICS: "/analytics",
  ACTIVITY: "/activity",
  LOGIN: "/login",

  BACKEND_PREFIX:
    "/sv74p3SUsqBsaGhiFGF4XkkAr1yTf9h5wZMEqvcugSjx1Mtg6upGxdJq20NOzx7l/backend",
  /** Paths relative to API_BASE_URL (which already ends with `/api`). */
  API: {
    SITE_TEXTS: "/site-texts/",
    APP_SETTINGS: "/app-settings/",
    RECORD_BUNDLE: "/record-bundle",
    recordBundleFile: (id: number) => `/record-bundle/${id}`,
    PROJECTS: "/projects",
    project: (id: string | number) => `/projects/${id}`,
    projectStats: (id: string | number) => `/projects/${id}/stats`,
    projectClone: (id: string | number) => `/projects/${id}/clone`,
    projectLive: (id: string | number) => `/projects/${id}/live`,
    projectActivity: (id: string | number) => `/projects/${id}/activity`,
    LOGIN: "/login",
    PROPERTY: "/property",
    property: (id: string | number) => `/property/${id}`,
    CALLS: "/calls",
    /** POST `{ ids }` to delete many calls in one request. */
    CALLS_BULK_DELETE: "/calls/bulk-delete",
    /** GET context and POST one or more operator-started calls. */
    MAKE_CALLS: "/calls/make-calls",
    DIALING_ACTIVITY: "/dialing-activity",
    PLAN_MEASUREMENTS: "/plan-measurements",
    call: (id: string | number) => `/calls/${id}`,
    /** Single payload for the call edit modal (call + webhooks + property rows). */
    callForm: (id: string | number) => `/calls/${id}/form`,
    /** POST: generate or return cached AI explanation for a failed call. */
    callWhyFailed: (id: string | number) => `/calls/${id}/why-failed`,
    /** POST: re-run business-outcome AI classification. */
    callReclassify: (id: string | number) => `/calls/${id}/reclassify`,
    /** Authenticated redirect to a short-lived Vapi recording URL. */
    callRecording: (id: string | number) => `/calls/${id}/recording`,
    WEBHOOK_LOGS: "/webhook-logs",
    webhookLog: (id: string | number) => `/webhook-logs/${id}`,
    WEBHOOK_LOGS_SIMULATE: "/webhook-logs/simulate",
    PROPERTY_UPLOAD_EXCEL: "/property/upload-excel",
    /** GET export / count, POST record import for the current project's properties. */
    PROPERTY_EXCEL: "/property/excel",
    CALLS_EXCEL: "/calls/excel",
    DNC_PHONE_CHECKS_EXCEL: "/property/dnc-phone-checks/excel",
    WEBHOOK_LOGS_EXCEL: "/webhook-logs/excel",
    /** POST: normalize comma/whitespace-only text fields on this project's properties. */
    PROPERTY_PATCH_PLACEHOLDER_CELLS: "/property/patch-placeholder-cells",
    /** GET: count of properties that would be modified. */
    PROPERTY_PATCH_PLACEHOLDER_CELLS_PREVIEW:
      "/property/patch-placeholder-cells/preview",
    /** GET: append-only DNC phone check log (alt phones 2–5). */
    DNC_PHONE_CHECKS: "/property/dnc-phone-checks",
    /** GET: unchecked alt 2–5 + extra checked phones (bulk DNC pool). */
    UNCHECKED_ALT_PHONES_DNC: "/property/unchecked-alt-phones-dnc",
    /** DELETE: one DNC phone check log row by id. */
    dncPhoneCheck: (id: string | number) => `/property/dnc-phone-checks/${id}`,
    /** POST: bulk RealValidito DNC check for alt_phone_2–5 on all properties. */
    CHECK_ALT_PHONES_DNC: "/property/check-alt-phones-dnc",
    /** POST: RealValidito DNC for specific property phone rows (unchecked modal). */
    CHECK_PROPERTY_PHONES_DNC: "/property/check-property-phones-dnc",
    /** POST: RealValidito federal DNC lookup for one phone (no log write). */
    CHECK_NUMBER_DNC: "/property/check-number-dnc",
    /** POST: RealValidito DNC check for alt_phone_2–5 on one property row. */
    propertyCheckAltPhonesDnc: (propertyId: string | number) =>
      `/property/${propertyId}/check-alt-phones-dnc`,
    /** GET preview: prompt from Settings with Vapi variables filled for this property row. */
    propertyFilledPrompt: (propertyId: string | number) =>
      `/property/${propertyId}/filled-prompt/`,
    propertyClearDnc: (propertyId: string | number) =>
      `/property/${propertyId}/clear-dnc`,
    propertyMove: (propertyId: string | number) =>
      `/property/${propertyId}/move`,
    propertyClone: (propertyId: string | number) =>
      `/property/${propertyId}/clone`,
    propertyNextWindow: (
      projectId: string | number,
      propertyId: string | number,
    ) => `/projects/${projectId}/properties/${propertyId}/next-window`,
    propertyDebug: (projectId: string | number, propertyId: string | number) =>
      `/projects/${projectId}/properties/${propertyId}/debug`,
    propertyTimeline: (
      projectId: string | number,
      propertyId: string | number,
    ) => `/projects/${projectId}/properties/${propertyId}/timeline`,
    propertyTimelineRun: (
      projectId: string | number,
      propertyId: string | number,
      runKey: string,
    ) =>
      `/projects/${projectId}/properties/${propertyId}/timeline/runs/${encodeURIComponent(runKey)}`,
    propertyRequeue: (
      projectId: string | number,
      propertyId: string | number,
    ) => `/projects/${projectId}/properties/${propertyId}/requeue`,
    lostLeads: (projectId: string | number) =>
      `/projects/${projectId}/lost-leads`,
    projectAnalytics: (projectId: string | number) =>
      `/projects/${projectId}/analytics`,
    projectOutboundHealth: (projectId: string | number) =>
      `/projects/${projectId}/outbound-health`,
    /** GET/POST: SIP trunks (Zadarma / BYO SIP) managed via Vapi. */
    SIP_TRUNKS: "/sip-trunks",
    SIP_TRUNKS_SYNC_FROM_VAPI: "/sip-trunks/sync-from-vapi",
    sipTrunk: (id: string | number) => `/sip-trunks/${id}`,
    sipTrunkPhoneNumbers: (id: string | number) =>
      `/sip-trunks/${id}/phone-numbers`,
    sipPhoneNumber: (id: string | number) => `/sip-phone-numbers/${id}`,
    sipPhoneNumberTestCall: (id: string | number) =>
      `/sip-phone-numbers/${id}/test-call`,
    /** GET: Vapi assistants for SIP phone assignment. */
    VAPI_ASSISTANTS: "/vapi/assistants",
    /** GET/PATCH: Vapi assistant server URL (webhook) for this project. */
    VAPI_ASSISTANT_SERVER_URL: "/vapi/assistant-server-url",
    VAPI_EMULATOR_SETTINGS: "/vapi-emulator/settings/",
    VAPI_EMULATOR_ANSWERS: "/vapi-emulator/answers/",
    VAPI_EMULATOR_ANSWERS_REORDER: "/vapi-emulator/answers/reorder/",
    VAPI_EMULATOR_RESET_CURSOR: "/vapi-emulator/settings/reset-cursor/",
    VAPI_EMULATOR_LOGS: "/vapi-emulator/logs/",
    VAPI_EMULATOR_ONGOING_CALLS: "/vapi-emulator/ongoing-calls/",
    VAPI_EMULATOR_SEND_WEBHOOK: "/vapi-emulator/send-webhook/",
    vapiEmulatorAnswer: (id: string | number) =>
      `/vapi-emulator/answers/${id}/`,
    CALENDAR: "/calendar",
    CALENDAR_BOOKINGS: "/calendar/bookings",
    callingCalendar: (projectId: string | number) =>
      `/projects/${projectId}/calling-calendar`,
    callingCalendarEvent: (projectId: string | number, id: string | number) =>
      `/projects/${projectId}/calling-calendar/${id}`,
    calendarBooking: (id: string | number) => `/calendar/bookings/${id}`,
    GOOGLE_CALENDAR_SETTINGS: "/google-calendar/settings",
    GOOGLE_CALENDAR_OAUTH_START: "/google-calendar/oauth/start",
    GOOGLE_CALENDAR_SYNC_PULL: "/google-calendar/sync/pull",
    GOOGLE_CALENDAR_SYNC_PUSH: "/google-calendar/sync/push",
  },
};

/** Unscoped frontend path kept for bookmarks from before the property rename. */
export const LEGACY_CONTACTS_PAGE_PATH = "/contacts";

/** Deep-link query for opening a property row edit modal. */
export const PROPERTY_ROW_ID_QUERY_PARAM = "propertyId";
/** Bookmark compatibility after the property rename. */
export const LEGACY_CONTACT_ROW_ID_QUERY_PARAM = "contactId";

/** Deep-link query for opening the add-property modal (`add=1`). */
export const PROPERTY_ADD_QUERY_PARAM = "add";
export const PROPERTY_ADD_QUERY_VALUE = "1";

/** Deep-link query for opening the Excel upload modal (`excel=1`). */
export const PROPERTY_EXCEL_UPLOAD_QUERY_PARAM = "excel";
export const PROPERTY_EXCEL_UPLOAD_QUERY_VALUE = "1";

/** Deep-link query for opening a call row edit modal. */
export const CALL_ROW_ID_QUERY_PARAM = "callId";

/** Query param for pre-filling the Calls table Call ID column filter. */
export const CALLS_TABLE_ID_FILTER_QUERY_PARAM = "id";

/** Navigate to Calls with the Call ID column filter set (does not open the edit modal). */
export function callsFilteredByCallIdHref(
  projectId: number | string | null | undefined,
  callId: number | string,
): string {
  return `${projectPath(projectId, PATHS.CALLS)}?${CALLS_TABLE_ID_FILTER_QUERY_PARAM}=${encodeURIComponent(String(callId))}`;
}

export function propertyRowIdFromSearchParams(
  searchParams: URLSearchParams,
): string | null {
  return (
    searchParams.get(PROPERTY_ROW_ID_QUERY_PARAM) ??
    searchParams.get(LEGACY_CONTACT_ROW_ID_QUERY_PARAM)
  );
}

export function stripPropertyRowIdFromSearchParams(
  params: URLSearchParams,
): void {
  params.delete(PROPERTY_ROW_ID_QUERY_PARAM);
  params.delete(LEGACY_CONTACT_ROW_ID_QUERY_PARAM);
}

export function stripPropertyPageModalParams(params: URLSearchParams): void {
  stripPropertyRowIdFromSearchParams(params);
  params.delete(PROPERTY_ADD_QUERY_PARAM);
  params.delete(PROPERTY_EXCEL_UPLOAD_QUERY_PARAM);
}

function normalizePagePath(pagePath: string): string {
  if (!pagePath) {
    return "/";
  }
  return pagePath.startsWith("/") ? pagePath : `/${pagePath}`;
}

/** Route pattern for a project-scoped page, e.g. `/:projectId/property`. */
export function projectRoute(pagePath: string): string {
  return `/:projectId${normalizePagePath(pagePath)}`;
}

/**
 * Bookmarkable path for a project page (`/12/property`).
 * Falls back to the unscoped page when the id is missing or invalid.
 */
export function projectPath(
  projectId: number | string | null | undefined,
  pagePath: string,
): string {
  const suffix = normalizePagePath(pagePath);
  if (projectId == null || projectId === "") {
    return suffix;
  }
  const id = Number(projectId);
  if (!Number.isInteger(id) || id <= 0) {
    return suffix;
  }
  return `/${id}${suffix}`;
}

function stripProductionPrefix(
  pathname: string,
  productionPrefix: string,
): string {
  let path = pathname || "/";
  if (productionPrefix && path.startsWith(productionPrefix)) {
    path = path.slice(productionPrefix.length) || "/";
  }
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }
  return path;
}

/** Numeric project id at the start of a frontend path, or null. */
export function parseProjectIdFromPathname(
  pathname: string,
  productionPrefix = "",
): number | null {
  const path = stripProductionPrefix(pathname, productionPrefix);
  const match = path.match(/^\/(\d+)(?:\/|$)/);
  if (!match) {
    return null;
  }
  const id = Number(match[1]);
  if (!Number.isInteger(id) || id <= 0 || match[1] !== String(id)) {
    return null;
  }
  return id;
}

/**
 * Page suffix after an optional production prefix and `/<projectId>`.
 * `/12/property/` → `/property`; `/` → `/`.
 */
export function pagePathFromPathname(
  pathname: string,
  productionPrefix = "",
): string {
  let path = stripProductionPrefix(pathname, productionPrefix);
  if (path !== "/" && path.endsWith("/")) {
    path = path.slice(0, -1);
  }
  const match = path.match(/^\/\d+(\/.*)?$/);
  if (!match) {
    return path === LEGACY_CONTACTS_PAGE_PATH ? PATHS.PROPERTY : path;
  }
  const suffix = match[1] || "/";
  return suffix === LEGACY_CONTACTS_PAGE_PATH ? PATHS.PROPERTY : suffix;
}
